/**
 * Biometric Authentication Utilities
 * 
 * Provides Face ID, Touch ID, and other biometric authentication
 * using the Web Authentication API (WebAuthn)
 */

import { SESSION_CONFIG } from './session-config';
import { clientLogger } from '@/lib/client-logger';
const log = clientLogger.child('biometric');

export interface BiometricCapability {
  available: boolean;
  type: 'platform' | 'none'; // platform = Face ID/Touch ID
  platformAuthenticator: boolean;
}

/**
 * Check if biometric authentication is available on this device
 */
export async function checkBiometricAvailability(): Promise<BiometricCapability> {
  // Check if Web Authentication API is available
  if (typeof window === 'undefined' || !window.PublicKeyCredential) {
    return {
      available: false,
      type: 'none',
      platformAuthenticator: false,
    };
  }

  try {
    // Check if platform authenticator (Face ID/Touch ID) is available
    const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    
    return {
      available,
      type: available ? 'platform' : 'none',
      platformAuthenticator: available,
    };
  } catch (error) {
    log.error('Error checking availability', { err: error });
    return {
      available: false,
      type: 'none',
      platformAuthenticator: false,
    };
  }
}

/**
 * Register biometric credentials for a user
 * This should be called after successful password login
 */
export async function registerBiometric(userId: string, userEmail: string): Promise<boolean> {
  try {
    const capability = await checkBiometricAvailability();
    if (!capability.available) {
      throw new Error('Biometric authentication not available');
    }

    // Generate challenge (in production, this should come from server)
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    
    // Create credential
    const credential = await navigator.credentials.create({
      publicKey: {
        challenge,
        rp: {
          name: 'Aquivis Pool Management',
          id: window.location.hostname,
        },
        user: {
          id: new TextEncoder().encode(userId),
          name: userEmail,
          displayName: userEmail,
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },  // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          authenticatorAttachment: 'platform', // Use built-in biometrics
          userVerification: 'required',
          requireResidentKey: false,
        },
        timeout: 60000,
        attestation: 'none',
      },
    }) as PublicKeyCredential | null;

    if (!credential) {
      return false;
    }

    // Store credential ID for future authentication
    localStorage.setItem('aquivis_biometric_credential', credential.id);
    localStorage.setItem('aquivis_biometric_user_id', userId);
    
    return true;
  } catch (error) {
    log.error('Registration failed', { err: error });
    return false;
  }
}

/**
 * Authenticate using biometric credentials
 * Returns user ID if successful, null if failed or cancelled
 */
export async function authenticateWithBiometric(): Promise<string | null> {
  try {
    const capability = await checkBiometricAvailability();
    if (!capability.available) {
      return null;
    }

    const credentialId = localStorage.getItem('aquivis_biometric_credential');
    const userId = localStorage.getItem('aquivis_biometric_user_id');
    
    if (!credentialId || !userId) {
      return null;
    }

    // Generate challenge (in production, should come from server)
    const challenge = crypto.getRandomValues(new Uint8Array(32));
    
    // Authenticate
    const assertion = await navigator.credentials.get({
      publicKey: {
        challenge,
        rpId: window.location.hostname,
        allowCredentials: [{
          id: Uint8Array.from(atob(credentialId), c => c.charCodeAt(0)),
          type: 'public-key',
          transports: ['internal'],
        }],
        userVerification: 'required',
        timeout: 60000,
      },
    });

    if (!assertion) {
      return null;
    }

    // Mark that biometric auth was successful
    sessionStorage.setItem(SESSION_CONFIG.BIOMETRIC_PROMPT_KEY, 'authenticated');
    
    return userId;
  } catch (error: any) {
    // User cancelled or authentication failed
    if (error.name !== 'NotAllowedError') {
      log.error('Authentication failed', { err: error });
    }
    return null;
  }
}

/**
 * Check if biometric authentication is enrolled for current device
 */
export function isBiometricEnrolled(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(localStorage.getItem('aquivis_biometric_credential'));
}

/**
 * Remove biometric credentials (logout)
 */
export function clearBiometric(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('aquivis_biometric_credential');
  localStorage.removeItem('aquivis_biometric_user_id');
  sessionStorage.removeItem(SESSION_CONFIG.BIOMETRIC_PROMPT_KEY);
}

/**
 * Prompt user to enable biometric authentication
 * Returns true if user agreed and setup successful
 */
export async function promptBiometricSetup(userId: string, userEmail: string): Promise<boolean> {
  const capability = await checkBiometricAvailability();
  
  if (!capability.available) {
    return false;
  }

  // Check if already enrolled
  if (isBiometricEnrolled()) {
    return true;
  }

  // In a real implementation, you'd show a modal here
  // For now, return true to indicate setup should proceed
  return await registerBiometric(userId, userEmail);
}

/**
 * Get user-friendly name for biometric type on this device
 */
export function getBiometricName(): string {
  if (/iPhone|iPad|iPod/.test(navigator.userAgent)) {
    return 'Face ID or Touch ID';
  }
  if (/Android/.test(navigator.userAgent)) {
    return 'Fingerprint or Face Unlock';
  }
  return 'Biometric Authentication';
}
